/**
 * Fixtures structurally withheld from the board.
 *
 * WHY THIS SCREEN EXISTS AT ALL. `pending_refresh` — a clear has been asked for
 * and the fixture is STILL unplaceable until its own catalogue transaction
 * rewrites it — appears on no other surface in the console. Without this list
 * the one state nobody can otherwise see is the one state nobody can see.
 *
 * THE COPY IS THE FEATURE. Clearing a quarantine does not reopen a fixture; it
 * asks the catalogue to earn it back. An operator who reads "cleared" as
 * "reopened" tells someone the race is back on the board at the exact moment it
 * is guaranteed not to be — so every sentence here comes from `logic.ts`, where
 * it is pinned by tests, rather than being written inline.
 */
import { useState } from 'react';
import { useFixtureQuarantines, useApplyQuarantine, useClearQuarantine } from '@/hooks/useAdmin';
import { useMe } from '@/hooks/useMe';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Pill } from '@/components/ui/StatusPill';
import { ConfirmAction } from '@/components/ui/Modal';
import { AdminLoading, AdminError, AdminPageHeader } from './AdminAsync';
import {
  ROLE_SETS,
  isRoleAuthorized,
  validateFixtureId,
  validateReason,
  quarantineActions,
  describeQuarantineApply,
  describeQuarantineClear,
  describeQuarantineState,
  describeQuarantineExposure,
} from './logic';
import type { AdminFixtureQuarantine } from '@/types/api';

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-white/35">
        {label}
      </div>
      <div className="mt-0.5 text-sm text-white/80">{value}</div>
    </div>
  );
}

function when(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-AU');
}

function timeAgo(iso: string, now: number): string {
  const secs = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
  return `${Math.round(secs / 3600)}h ago`;
}

/** What the server was asked to do, shown in place of the form once it has. */
function Succeeded({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-brand-teal/35 bg-brand-teal/[0.07] p-3">
      <p className="text-sm leading-relaxed text-white/85">{children}</p>
    </div>
  );
}

/** Which dialog is open. `null` is none — the two are mutually exclusive. */
type Action = { kind: 'apply' } | { kind: 'clear'; row: AdminFixtureQuarantine } | null;

export function FixtureQuarantinePage() {
  const query = useFixtureQuarantines();
  const me = useMe();

  /*
    Hooks are called unconditionally, ABOVE the early returns below. A mutation
    hook behind an `if` is a different hook order on the render after the fetch
    resolves.
  */
  const apply = useApplyQuarantine();
  const clear = useClearQuarantine();

  const [action, setAction] = useState<Action>(null);
  const [fixtureId, setFixtureId] = useState('');
  const [reason, setReason] = useState('');

  /*
    Closing resets the MUTATION, not just the form. React Query keeps the last
    result on the mutation object, so a re-opened dialog would otherwise show
    the previous action's success panel over an untouched form.
  */
  function close() {
    setAction(null);
    apply.reset();
    clear.reset();
  }

  function openApply() {
    setFixtureId('');
    setReason('');
    apply.reset();
    clear.reset();
    setAction({ kind: 'apply' });
  }

  function openClear(row: AdminFixtureQuarantine) {
    setReason('');
    apply.reset();
    clear.reset();
    setAction({ kind: 'clear', row });
  }

  if (query.isLoading) return <AdminLoading label="Reading withheld fixtures" />;
  if (query.isError || !query.data)
    return <AdminError error={query.error} onRetry={() => void query.refetch()} />;

  const rows = query.data.quarantines;
  /* Age is relative to the fetch, not to render — see MarketVisibilityPage. */
  const now = query.dataUpdatedAt;

  /*
    The list is ANY_ADMIN; both writes are WRITE. Support staff can therefore
    legitimately read this screen and must not be offered a button the server
    is certain to refuse. This is DISPLAY ONLY — the API re-checks the claim.
  */
  const canWrite = isRoleAuthorized(me.data?.user.role ?? null, ROLE_SETS.WRITE);

  const idError = action?.kind === 'apply' ? validateFixtureId(fixtureId) : null;
  const reasonError = validateReason(reason);

  return (
    <>
      <AdminPageHeader
        title="Fixture quarantines"
        subtitle="Fixtures structurally withheld from the board. Clearing one does not reopen it — it stays unplaceable until that fixture's own catalogue sync rewrites it successfully."
        actions={
          canWrite ? (
            <Button size="sm" onClick={openApply}>
              Withhold a fixture
            </Button>
          ) : undefined
        }
      />

      {rows.length === 0 ? (
        <Card>
          <p className="text-sm leading-relaxed text-white/55">
            No fixture is withheld. Nothing is being held off the board and no clear is waiting on
            a catalogue sync.
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {rows.map((row) => {
            const actions = quarantineActions(row.state);
            return (
              <Card key={row.fixtureId}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-display text-sm font-bold tracking-tight">
                        {row.eventName ?? 'Unnamed fixture'}
                      </span>
                      <Pill tone={row.state === 'active' ? 'caution' : 'neutral'}>
                        {describeQuarantineState(row.state)}
                      </Pill>
                    </div>
                    <p className="mt-1 font-mono text-[11px] text-white/35">{row.fixtureId}</p>
                    <p className="mt-2 text-sm leading-relaxed text-white/70">
                      {describeQuarantineExposure(row.pendingBets, row.affectedMarkets)}
                    </p>
                  </div>

                  {canWrite && actions.canClear && (
                    <Button variant="secondary" size="sm" onClick={() => openClear(row)}>
                      Request clear
                    </Button>
                  )}
                </div>

                <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <Field label="Sport" value={row.sportKey ?? '—'} />
                  <Field label="League" value={row.league ?? '—'} />
                  <Field
                    label="Teams"
                    value={row.home && row.away ? `${row.home} v ${row.away}` : '—'}
                  />
                  <Field label="Kickoff" value={when(row.kickoff)} />
                  <Field
                    label="Withheld"
                    value={`${timeAgo(row.quarantinedAt, now)} · ${when(row.quarantinedAt)}`}
                  />
                  <Field label="By" value={<span className="font-mono text-xs">{row.quarantinedBy}</span>} />
                  <Field
                    label="Clear requested"
                    value={
                      row.clearRequestedAt
                        ? `${timeAgo(row.clearRequestedAt, now)} · ${when(row.clearRequestedAt)}`
                        : '—'
                    }
                  />
                  <Field
                    label="Clear requested by"
                    value={
                      row.clearRequestedBy ? (
                        <span className="font-mono text-xs">{row.clearRequestedBy}</span>
                      ) : (
                        '—'
                      )
                    }
                  />
                </div>

                <div className="mt-3">
                  <Field label="Reason" value={row.reason} />
                </div>

                {actions.canReapply && (
                  <p className="mt-3 rounded-xl border border-white/[0.08] bg-white/[0.03] p-3 text-xs leading-relaxed text-white/50">
                    A clear is already pending. There is nothing further to press — this row
                    disappears from the list when this fixture&rsquo;s own catalogue transaction
                    rewrites it and releases it.
                  </p>
                )}
              </Card>
            );
          })}
        </div>
      )}

      <ConfirmAction
        open={action?.kind === 'apply'}
        title="Withhold a fixture from the board"
        confirmLine={
          idError
            ? 'Enter the fixture id to withhold. Its markets are suspended immediately, it stops appearing in /markets, and placement refuses it. Bets already struck on it are not affected.'
            : describeQuarantineApply(fixtureId.trim())
        }
        fieldError={idError}
        confirmLabel="Withhold fixture"
        danger
        pending={apply.isPending}
        error={apply.error}
        reason={reason}
        onReasonChange={setReason}
        reasonError={reasonError}
        onClose={close}
        onConfirm={(note) => apply.mutate({ fixtureId: fixtureId.trim(), reason: note })}
        /*
          Composed from what the operator ASKED for, not from the response: the
          contract declares no response content for the 201, so there is no
          server-stated outcome to render. The list is re-read by the hook.
        */
        success={
          apply.isSuccess ? (
            <Succeeded>
              {fixtureId.trim()} is withheld. Its markets are suspended, it is off the board, and
              placement refuses it. The list has been re-read.
            </Succeeded>
          ) : undefined
        }
      >
        <div>
          <label
            htmlFor="quarantine-fixture-id"
            className="text-sm font-medium text-white/80"
          >
            Fixture id
          </label>
          <input
            id="quarantine-fixture-id"
            value={fixtureId}
            onChange={(e) => setFixtureId(e.target.value)}
            placeholder="16 lowercase hex characters"
            spellCheck={false}
            autoComplete="off"
            className={`mt-1 w-full rounded-lg border bg-brand-surface px-3 py-2 font-mono text-sm text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-brand-teal ${
              idError ? 'border-red-500' : 'border-white/20'
            }`}
          />
          <p className="mt-1 text-xs text-white/35">
            Copied from the catalogue-sync log line or the odds-engine screen.
          </p>
        </div>
      </ConfirmAction>

      <ConfirmAction
        open={action?.kind === 'clear'}
        title="Request a clear"
        confirmLine={
          action?.kind === 'clear'
            ? describeQuarantineClear(action.row.fixtureId, action.row.eventName)
            : ''
        }
        confirmLabel="Request clear"
        pending={clear.isPending}
        error={clear.error}
        reason={reason}
        onReasonChange={setReason}
        reasonError={reasonError}
        onClose={close}
        onConfirm={(note) => {
          if (action?.kind !== 'clear') return;
          clear.mutate({ fixtureId: action.row.fixtureId, body: { reason: note } });
        }}
        success={
          clear.isSuccess && action?.kind === 'clear' ? (
            <Succeeded>
              The clear is recorded against {action.row.fixtureId}. It is STILL withheld — its
              markets stay suspended and placement keeps refusing it until that fixture&rsquo;s own
              catalogue sync rewrites it successfully. It leaves this list when that happens.
            </Succeeded>
          ) : undefined
        }
      />
    </>
  );
}
